<template>
  <section class="invoice-preview-wrapper">

    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="invoiceData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching invoice data
      </h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'apps-invoices-list'}"
        >
          Invoice List
        </b-link>
        for other invoices.
      </div>
    </b-alert>

    <b-row
      v-if="invoiceData"
      class="invoice-preview"
    >

      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          ref="payroll-preview"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">

            <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

              <!-- Header: Left Content -->
              <div>
                <div class="logo-wrapper">
                  <span class="brand-logo">
                    <b-img
                      :src="require(`@hw-ui/assets/images/logo/${this.$store.state.app.appConfig.logoName}`)"
                      style="width: 50px"
                      alt="logo"
                    />
                  </span>
                  <h3
                    class="invoice-logo"
                  >
                    {{ this.$store.state.app.appConfig.name }}
                  </h3>
                </div>
              </div>

              <!-- Header: Right Content -->
              <div class="mt-md-2 mt-2">
                <h4 class="invoice-title">
                  {{$t('payslip')}}
                </h4>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date">
                    {{ `${invoiceData.from.toDate().toLocaleDateString('fi')} - ${invoiceData.to.toDate().toLocaleDateString('fi')}` }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Invoice Client & Payment Details -->
          <b-card-body
            v-if="invoiceData.user"
            class="invoice-padding pt-0"
          >
            <b-row class="invoice-spacing">

              <!-- Col: Invoice To -->
              <b-col
                cols="12"
                xl="6"
                class="p-0"
              >
                <h6
                  class="mb-25"
                  style="text-transform: uppercase"
                >
                  {{ invoiceData.user.fullName }}
                </h6>
                <p class="card-text mb-25">
                  {{ invoiceData.user.email }}
                </p>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite
            fixed
            :items="invoiceData.items"
            :fields="[{ key: 'title', label: 'Peruspalkka'}, { key: 'qty', label: $t('amount')}, { key: 'unit', label: $t('unit')}, { key: 'cost', label: $t('cost')}, { key: 'total', label: $t('total')}]"
          >
            <template #cell(qty)="data">
              <b-card-text class="mb-25">
                {{ parseFloat(data.item.qty).toFixed(2) }}
              </b-card-text>
            </template>
            <template #cell(cost)="data">
              <b-card-text class="mb-25">
                {{ parseFloat(data.item.cost).toFixed(2) }} €
              </b-card-text>
            </template>
            <template #cell(total)="data">
              <b-card-text class="mb-25">
                {{ data.item.total.toFixed(2) }} €
              </b-card-text>
            </template>
          </b-table-lite>
          <b-table-lite
            v-if="invoiceData.nonTaxable.length > 0"
            fixed
            :items="invoiceData.nonTaxable"
            :fields="[{ key: 'title', label: 'Korvaus'}, { key: 'qty', label: $t('amount')}, { key: 'unit', label: $t('unit')}, { key: 'cost', label: $t('cost')}, { key: 'total', label: $t('total')}]"
          >
            <template #cell(qty)="data">
              <b-card-text class="mb-25">
                {{ parseFloat(data.item.qty).toFixed(2) }}
              </b-card-text>
            </template>
            <template #cell(cost)="data">
              <b-card-text class="mb-25">
                {{ parseFloat(data.item.cost).toFixed(2) }} €
              </b-card-text>
            </template>
            <template #cell(total)="data">
              <b-card-text class="mb-25">
                {{ data.item.total.toFixed(2) }} €
              </b-card-text>
            </template>
          </b-table-lite>
          <div
            v-if="invoiceData.holidayPay"
          >
            <hr>
            <b-table-lite
              :items="[{ title:'Lomakorvaus', amount: '9%', spacer: '%', price: invoiceData.subTotal.toFixed(2) + '€', total: (invoiceData.subTotal * invoiceData.holidayPay).toFixed(2) + '€' }]"
              :fields="[{ key:'title', label: $t('bonus') }, { key:'amount', label: $t('amount')}, { key:'spacer', label: $t('unit')}, { key:'price', label: $t('from_price')}, { key:'total', label: $t('total')}]"
            />
          </div>
          <!-- Spacer -->
          <hr class="invoice-spacing">
          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0">
            <b-row>
              <!-- Col: Sales Persion -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3"
                order="2"
                order-md="1"
              />
              <!-- Col: Total -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-6 d-flex justify-content-end"
                order="1"
                order-md="2"
              >
                <div class="invoice-total-wrapper">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      {{$t('total_VAT_free')}}:
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoiceData.total }}€
                    </p>
                  </div>
                  <hr class="my-50">
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <b-button
            v-if="invoiceData.status !== 'paid'"
            variant="primary"
            class="mb-75"
            block
            @click="markAsPaid(true)"
          >
            {{ $t('payroll.btn.markAsPaid') }}
          </b-button>
          <b-button
            :disabled="sending"
            variant="info"
            class="mb-75"
            block
            @click="sendInvoice(invoiceData)"
          >
            <b-spinner
              v-if="sending"
              small
            />
            <span
              v-if="!sending"
            >
              {{ $t('payroll.btn.send') }}
            </span>
          </b-button>
          <hr>
          <!-- Button: Edit -->
          <b-button
            variant="outline-primary"
            class="mb-75"
            block
            :to="{ name: 'apps-payrolls-edit', params: { id: $route.params.id } }"
          >
            {{ $t('edit') }}
          </b-button>
          <b-button
            :disabled="downloading"
            variant="outline-info"
            class="mb-75"
            block
            @click="downloadInvoice(invoiceData)"
          >
            <b-spinner
              v-if="downloading"
              small
            />
            <span
              v-if="!downloading"
            >
              {{ $t('payroll.btn.downloadPDF') }}
            </span>
          </b-button>
          <b-button
            v-if="invoiceData.status === 'paid'"
            variant="outline-warning"
            class="mb-75"
            block
            @click="markAsPaid(false)"
          >
            {{ 'payroll.btn.notPaid' }}
          </b-button>
          <!-- Button: close -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="$router.back()"
          >
            {{ $t('close') }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <invoice-sidebar-send-invoice />
    <invoice-sidebar-add-payment />
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import router from '@/shared/router'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import {
  BRow, BCol, BCard, BCardBody, BTableLite, BCardText, BButton, BAlert, BLink, VBToggle,
  BImg, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useToast } from 'vue-toastification/composition'
import html2pdf from 'html2pdf.js'
import {
  fetchPayroll,
  markAsPaid as markAsPaidSv, sendPayrolls,
} from '@/shared/services/payrollService'
import InvoiceSidebarSendInvoice from '../InvoiceSidebarSendInvoice.vue'
import InvoiceSidebarAddPayment from '../InvoiceSidebarAddPayment.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    BSpinner,
    BImg,
    InvoiceSidebarAddPayment,
    InvoiceSidebarSendInvoice,
  },
  data() {
    return ({
      sending: false,
      downloading: false,
    })
  },
  methods: {
    async sendInvoice(invoice) {
      this.sending = true
      try {
        sendPayrolls([invoice])
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('success'),
            text: 'Palkkalaskelma lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.back()
      } catch {
        this.sending = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('error'),
            text: this.$t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async downloadInvoice(invoice) {
      /* eslint-disable new-cap */
      this.downloading = true
      const element = this.$refs['payroll-preview']
      await html2pdf().set({ margin: 10 }).from(element).save(`palkka_${invoice.user.fullName}_ ${invoice.from.toDate().toLocaleDateString('fi')}.pdf`)
      this.downloading = false
    },
  },
  setup() {
    const invoiceData = ref(null)
    const toast = useToast()

    fetchPayroll({ id: router.currentRoute.params.id })
      .then(response => {
        invoiceData.value = response
      })
      .catch(error => {
        if (error.response.status === 404) {
          invoiceData.value = undefined
        }
      })

    const markAsPaid = value => {
      const { id } = router.currentRoute.params
      const status = value ? 'paid' : 'pending'
      markAsPaidSv({ id, status })
        .then(() => {
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('success'),
              text: 'Tila päivitetty',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.back()
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('error'),
              text: this.$t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    return {
      invoiceData,
      markAsPaid,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@hw-ui/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {

  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
              > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
